import { useEffect, useState, useRef } from 'react';
import { Context } from '@alltrails/shared/types/context';
import TrailDetailsCard from 'components/cards/TrailDetailsCard';
import NearbyTrailsList from 'components/NearbyTrailsList';
import ViewFullMap from 'components/shared/ViewFullMap';
import SignupModal from 'components/SignupModal';
import Link from '@alltrails/shared/denali/components/Link';
import Toast from '@alltrails/shared/denali/components/Toast';
import User from '@alltrails/shared/types/User';
import TrailActionBar from 'components/TrailActionBar';
import AdminTrailDropdown from 'components/TrailDetails/AdminTrailDropdown';
import FlyoverEntry from 'components/TrailDetails/FlyoverEntry';
import ProgrammaticOverview from 'components/TrailDetails/ProgrammaticOverview';
import TagGroup from '@alltrails/shared/denali/components/TagGroup';
import TrailConditions from 'components/TrailDetails/TrailConditions';
import TrailOverview from 'components/TrailDetails/TrailOverview';
import WeatherConditions from 'components/TrailDetails/WeatherConditions';
import TrailFaq from 'components/TrailFaq';
import TrailStats from 'components/TrailStats';
import TrailUgc from 'components/trailUgc/TrailUgc';
import { useTrailUgcContext } from 'components/trailUgc/TrailUgcContext';
import RatingsBreakdown from 'components/trailUgc/types/RatingsBreakdown';
import { HAWAII_ID, UNITED_STATES_ID } from 'constants/CountryIDs';
import logTrailPageViewLimitReached from '@alltrails/analytics/events/logTrailPageViewLimitReached';
import useToggle from '@alltrails/shared/hooks/useToggle';
import { debounce } from 'debounce';
import useFeatures from 'hooks/useFeatures';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import ListMethods from 'types/ListMethods';
import { Photo } from 'types/Photo';
import { Trail as TrailType, TrailId } from 'types/Trails/Trail';
import { getTagGroupArray } from 'utils/trail_helpers';
import classNames from 'classnames';
import { logTrailDetailsScrollDepth } from '../../utils/trail_analytics_helpers';
import * as styles from './Trail.module.scss';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';

export type TrailTabType = {
  description_lang?: string;
  key: string;
  label: string;
  maxLength: number;
  sourceText?: string;
  value: any;
};

type Props = {
  adminOrPendingOwner: boolean;
  avgRating?: string;
  context: Context;
  descriptionInitiallyExpanded: boolean;
  exactExplorePath?: string;
  exploreFlag?: boolean;
  explorePath?: string;
  initializingFlyover?: boolean;
  listMethods?: ListMethods;
  locationServerData?: unknown;
  maxAllowedTrailCount: number;
  overview?: string;
  offerMergeEnds?: unknown;
  trailTags?: {
    whoToBring: any;
    whatToDo: any;
    whatToSeeAndObstacles: any;
  };
  nearbyTrails: TrailType[];
  trail: TrailType;
  numPhotos?: number;
  pending?: boolean;
  photoId: number;
  photoUrl?: string;
  webpStaticMapUrl?: string;
  popTrailReview?: boolean;
  webpPhotoUrl?: string;
  staticMapUrl?: string;
  trailServerData?: unknown;
  overviewProps: {
    tabs: Array<TrailTabType>;
  };
  routeType?: string;
  trailPageLimitReached: boolean;
  trailConditions?: any[];
  ratingsBreakdown?: RatingsBreakdown;
  onFlyoverClick?: () => void;
  shareObject?: unknown;
  weatherConditionsProps: any;
  photos?: Photo[];
};

export default function Trail({
  context,
  exploreFlag,
  ratingsBreakdown,
  avgRating,
  trail,
  locationServerData,
  nearbyTrails,
  adminOrPendingOwner,
  photos,
  popTrailReview,
  weatherConditionsProps,
  routeType,
  overviewProps,
  overview,
  offerMergeEnds,
  trailTags,
  explorePath,
  exactExplorePath,
  photoId,
  pending,
  numPhotos,
  staticMapUrl,
  webpStaticMapUrl,
  photoUrl,
  webpPhotoUrl,
  listMethods,
  shareObject,
  trailServerData,
  trailConditions,
  descriptionInitiallyExpanded,
  trailPageLimitReached,
  maxAllowedTrailCount,
  initializingFlyover,
  onFlyoverClick
}: Props) {
  const intl = useIntl();
  const features = useFeatures();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const { allUgcShown, selectedUgcTab } = useTrailUgcContext().ugcState;

  const [isOpen, toggle] = useToggle(trailPageLimitReached);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const previousUser = useRef<User>(user);

  useEffect(() => {
    if (!previousUser.current && user) {
      setIsToastOpen(true);
    }

    previousUser.current = user;
  }, [setIsToastOpen, user]);

  useEffect(() => {
    if (isToastOpen) {
      setTimeout(() => {
        setIsToastOpen(false);
      }, 3000);
    }
  }, [isToastOpen]);

  useEffect(() => {
    if (trailPageLimitReached) {
      logTrailPageViewLimitReached({ limit: maxAllowedTrailCount, trail_id: trail.trail_id });
    }
  }, [trailPageLimitReached, maxAllowedTrailCount, trail.trail_id]);

  useEffect(() => {
    const handleScroll = debounce(() => {
      logTrailDetailsScrollDepth(trail.ID as TrailId, selectedUgcTab, allUgcShown);
    }, 300);

    if (!exploreFlag) {
      document.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (!exploreFlag) {
        document.removeEventListener('scroll', handleScroll);
      }
    };
  }, [allUgcShown, exploreFlag, selectedUgcTab, trail.ID]);

  function renderRobotsTag() {
    if (pending) {
      return <meta content="noindex" name="robots" />;
    }
    return null;
  }

  function renderNearbyTrails() {
    if (!nearbyTrails || nearbyTrails.length === 0) {
      return null;
    }

    return (
      <NearbyTrailsList
        context={context}
        explorePath={explorePath}
        listMethods={listMethods}
        locationServerData={trailServerData}
        nearbyTrails={nearbyTrails}
        trailId={trail.trail_id}
      />
    );
  }

  function renderTrailSidebar() {
    if (exploreFlag) {
      return null;
    }

    let adminDropdown = null;

    if (user?.admin) {
      adminDropdown = <AdminTrailDropdown offerMergeEnds={offerMergeEnds} statusPending={pending} trailId={trail.trail_id} />;
    }

    return (
      <div className={styles.trailSidebar}>
        <aside className="trail-aside">
          <ViewFullMap
            href={exactExplorePath}
            lat={trail._geoloc.lat}
            lng={trail._geoloc.lng}
            staticMapUrl={staticMapUrl}
            trail={trail}
            webpStaticMapUrl={webpStaticMapUrl}
          />
          <div className={styles.suggestEditAndDropdown}>
            <Link
              href={`/${trail.slug}/edit?ref=trail-suggest-edit`}
              rel={['nofollow', 'noreferrer']}
              target="_blank"
              testId="trail-suggest-edit"
              size="sm"
            >
              <FormattedMessage defaultMessage="Suggest edit" />
            </Link>
            {adminDropdown}
          </div>
          {renderNearbyTrails()}
        </aside>
      </div>
    );
  }

  function renderMobileMapSection() {
    if (exploreFlag) {
      return null;
    }

    return (
      <div className={styles.mobileStaticMap}>
        <ViewFullMap
          href={exactExplorePath}
          lat={trail._geoloc.lat}
          lng={trail._geoloc.lng}
          staticMapUrl={staticMapUrl}
          trail={trail}
          webpStaticMapUrl={webpStaticMapUrl}
        />
      </div>
    );
  }

  const userId = user ? user.id : null;

  const localBusinessDataAttributes = {
    itemType: 'http://schema.org/LocalBusiness',
    itemScope: true,
    itemID: `/${trail.slug}`
  };

  const tags = getTagGroupArray(trailTags);

  return (
    <>
      <SignupModal
        trigger={CarouselDisplayTrigger.TrailDetailsWall3rdUniqueViewed}
        isOpen={isOpen}
        onRequestClose={() => {
          global.history.back();
        }}
        onSuccess={toggle}
      />
      <div className={classNames(styles.container, 'xlate-none')} {...(trail.num_reviews ? localBusinessDataAttributes : {})}>
        {renderRobotsTag()}
        <TrailDetailsCard
          exploreFlag={exploreFlag}
          isPending={pending}
          listMethods={listMethods}
          numPhotos={numPhotos}
          photoId={photoId}
          photos={photos}
          photoUrl={photoUrl}
          trail={trail}
          webpPhotoUrl={webpPhotoUrl}
        />
        <TrailActionBar
          context={context}
          exploreFlag={exploreFlag}
          numPhotos={numPhotos}
          pending={pending}
          photos={photos}
          shareObject={shareObject}
          trail={trail}
        />
        {isToastOpen && (
          <Toast
            message={<FormattedMessage defaultMessage="Welcome, {name}!" values={{ name: user?.firstName }} />}
            position="alternative"
            type="success"
            testId="welcome_toast"
          />
        )}
        <div className={styles.trailInfoContainer}>
          <article className={styles.trailInfo}>
            <TrailStats displayMetric={context.displayMetric} languageRegionCode={languageRegionCode} routeType={routeType} trail={trail} />
            <ProgrammaticOverview overview={overview} trailId={trail.trail_id} />
            {features.includes('trail_flyover') && (
              <FlyoverEntry
                exactExplorePath={exactExplorePath}
                exploreFlag={exploreFlag}
                initializingFlyover={initializingFlyover}
                onEntryClick={onFlyoverClick}
                trail={trail}
              />
            )}
            <div className={styles.divider} />
            <div className={styles.tagGroupContainer}>
              <TagGroup tags={tags} testId="tag-group" />
            </div>
            {renderMobileMapSection()}
            <TrailOverview
              adminOrPendingOwner={adminOrPendingOwner}
              context={context}
              descriptionInitiallyExpanded={descriptionInitiallyExpanded}
              exploreFlag={exploreFlag}
              loggedInUser={!!user}
              tabs={overviewProps.tabs}
              trailId={trail.trail_id}
            />
            {languageRegionCode === 'en-US' && (trail.country_id === UNITED_STATES_ID || trail.country_id === HAWAII_ID) && (
              <TrailConditions exploreFlag={exploreFlag} trail={trail} trailConditions={trailConditions} /> /* RTTC is a US-only feature */
            )}
            <WeatherConditions
              latitude={trail._geoloc.lat}
              locale={languageRegionCode}
              longitude={trail._geoloc.lng}
              metric={context.displayMetric}
              tabs={weatherConditionsProps.tabs}
              trailId={trail.trail_id}
              weatherForecast={weatherConditionsProps.weatherForecast}
              intl={intl}
            />
            <section id="activity-feeds">
              <TrailUgc
                adminOrPendingOwner={adminOrPendingOwner}
                avgRating={avgRating}
                context={context}
                locationServerData={locationServerData}
                popTrailReview={popTrailReview}
                ratingsBreakdown={ratingsBreakdown}
                section="trail-show"
                trailId={trail.trail_id}
                trailName={trail.name}
                userId={userId}
              />
            </section>
            <TrailFaq trailName={trail.name} trailOverviewTabs={overviewProps.tabs} />
          </article>
          {renderTrailSidebar()}
        </div>
      </div>
    </>
  );
}
